// Paylaşılan görsel ilkeller — tasarımda (Call Agent Admin.dc.html) defalarca
// tekrar eden style blokları. Değerler birebir korunmuştur; tek kaynak burasıdır.

import React from 'react';

// ── Ikon ─────────────────────────────────────────────────────────────────────
// Material Symbols ligature ile çalışır: metin düğümü ikon adıdır.
// DİKKAT: i18n katmanı (i18n.ts) DOM'daki metin düğümlerini çevirir; ikon adları
// sözlükte olmadığı için dokunulmadan kalır.

export function Icon({
  name,
  size = 20,
  color,
  style,
}: {
  name: string;
  size?: number | string;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        fontFamily: "'Material Symbols Outlined'",
        fontSize: typeof size === 'number' ? size + 'px' : size,
        lineHeight: 1,
        color,
        flex: 'none',
        ...style,
      }}
    >
      {name}
    </span>
  );
}

// ── Stil yardımcıları ────────────────────────────────────────────────────────

/** Segment/preset butonu (Dönem, Kırılım, Ayarlar sekmeleri). */
export const segStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 13px',
  borderRadius: '9px',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  border: '1px solid ' + (active ? 'var(--accent-border)' : 'var(--border)'),
  background: active ? 'var(--accent-soft)' : 'transparent',
  color: active ? 'var(--text)' : 'var(--dim)',
  transition: 'all .15s',
});

/** Küçük marka çipi (Ayarlar / kullanıcı modali). */
export const chipStyle = (on: boolean): React.CSSProperties => ({
  fontSize: '11px',
  fontWeight: 600,
  padding: '4px 11px',
  borderRadius: '999px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  color: on ? 'var(--text)' : 'var(--dim)',
  background: on ? 'var(--accent-soft)' : 'transparent',
  border: '1px solid ' + (on ? 'var(--accent-border)' : 'var(--border)'),
});

export const switchStyle = (on: boolean): React.CSSProperties => ({
  width: '38px',
  height: '22px',
  borderRadius: '999px',
  background: on ? '#3d8bff' : 'var(--border2)',
  position: 'relative',
  cursor: 'pointer',
  transition: 'background .15s',
  flex: 'none',
});

export const knobStyle = (on: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: '2px',
  left: on ? '18px' : '2px',
  width: '18px',
  height: '18px',
  borderRadius: '999px',
  background: '#fff',
  transition: 'left .15s',
});

export const checkboxStyle = (on: boolean): React.CSSProperties => ({
  width: '20px',
  height: '20px',
  borderRadius: '6px',
  border: '1px solid ' + (on ? '#3d8bff' : 'var(--border2)'),
  background: on ? '#3d8bff' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flex: 'none',
});

/** Uyarı listesindeki küçük seçim kutusu (18px). */
export const smallCheckStyle = (on: boolean): React.CSSProperties => ({
  width: '18px',
  height: '18px',
  borderRadius: '5px',
  border: '1px solid ' + (on ? '#3d8bff' : 'var(--border2)'),
  background: on ? '#3d8bff' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flex: 'none',
});

/** Renk noktası (durum/sonuç göstergesi). */
export const dotStyle = (color: string, size = 7): React.CSSProperties => ({
  width: size + 'px',
  height: size + 'px',
  borderRadius: '999px',
  background: color,
  flex: 'none',
});

export const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
};

export const inputStyle: React.CSSProperties = {
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '11px 13px',
  color: 'var(--text)',
  fontSize: '13.5px',
  outline: 'none',
  width: '100%',
};

export const dateInputStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '9px 11px',
  color: 'var(--text)',
  fontSize: '12.5px',
  outline: 'none',
  colorScheme: 'dark',
};

export const primaryBtnStyle: React.CSSProperties = {
  padding: '11px',
  background: 'var(--accent)',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

export const ghostBtnStyle: React.CSSProperties = {
  padding: '11px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  color: 'var(--dim)',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

// ── Bileşenler ───────────────────────────────────────────────────────────────

const shimmer: React.CSSProperties = {
  background: 'linear-gradient(90deg,var(--surface),var(--shimmer),var(--surface))',
  backgroundSize: '200% 100%',
  animation: 'caaShimmer 1.3s linear infinite',
};

/**
 * Yükleniyor iskeleti — tasarımdaki 56/150/56 px üçlü ritim.
 * radius/gap yalnızca farklı ölçü kullanan ekranlar için (Uyarılar: 70px satırlar,
 * 10px yarıçap ve boşluk).
 */
export function Skeleton({ heights = [56, 150, 56], radius, gap = 12 }: { heights?: number[]; radius?: number; gap?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: gap + 'px' }}>
      {heights.map((h, i) => (
        <div key={i} style={{ height: h + 'px', borderRadius: (radius ?? (h > 80 ? 14 : 12)) + 'px', ...shimmer }} />
      ))}
    </div>
  );
}

/** Boş durum kutusu (kesikli kenarlık + ikon + başlık + sebep). */
export function EmptyState({
  icon,
  title,
  reason,
  iconColor = 'var(--faint)',
  minHeight = 200,
}: {
  icon: string;
  title: string;
  reason: string;
  iconColor?: string;
  minHeight?: number;
}) {
  return (
    <div
      style={{
        border: '1px dashed var(--border2)',
        borderRadius: '16px',
        minHeight: minHeight + 'px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px',
        color: 'var(--dim)',
      }}
    >
      <Icon name={icon} size={34} color={iconColor} style={{ marginBottom: '12px' }} />
      <div style={{ fontSize: '14px', color: 'var(--text)', fontWeight: 600, marginBottom: '5px' }}>{title}</div>
      <div style={{ fontSize: '13px', maxWidth: '360px', lineHeight: 1.5 }}>{reason}</div>
    </div>
  );
}

/**
 * Modal kabuğu. Arka plana tıklama kapatır; diyalog gövdesindeki tıklama
 * yayılmaz. Escape App seviyesinde dinlenir (tasarımdaki onKeyDown ile aynı).
 */
export function Modal({
  onClose,
  maxWidth = 420,
  children,
}: {
  onClose: () => void;
  maxWidth?: number;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={onClose}
      data-modal="1"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(3,6,12,.66)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 100,
        animation: 'caaFade .14s ease both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: '100%',
          maxWidth: maxWidth + 'px',
          background: 'var(--surface)',
          border: '1px solid var(--border2)',
          borderRadius: '16px',
          padding: '22px',
          boxShadow: '0 24px 60px rgba(0,0,0,.6)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Yerleşik ok işaretli <select> (appearance:none + expand_more). */
export function Select({
  value,
  onChange,
  options,
  compact,
  autoFocus,
  style,
  fullWidth,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  compact?: boolean;
  autoFocus?: boolean;
  style?: React.CSSProperties;
  fullWidth?: boolean;
}) {
  return (
    <div style={{ position: 'relative', width: fullWidth ? '100%' : undefined }}>
      <select
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          width: fullWidth ? '100%' : undefined,
          background: compact ? 'var(--inset)' : 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: compact ? '9px' : '10px',
          padding: compact ? '9px 30px 9px 12px' : '10px 34px 10px 13px',
          color: 'var(--text)',
          fontSize: compact ? '12.5px' : '13px',
          fontWeight: compact ? 400 : 500,
          cursor: 'pointer',
          outline: 'none',
          ...style,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: 'var(--surface)', color: 'var(--text)' }}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon
        name="expand_more"
        size={compact ? 17 : 18}
        color="var(--faint)"
        style={{
          position: 'absolute',
          right: compact ? '9px' : '11px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

/** Bölüm başlığı: numaralı rozet + başlık + alt başlık (Raporlar). */
export function SectionTitle({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <>
      <span
        style={{
          width: '26px',
          height: '26px',
          borderRadius: '8px',
          background: 'var(--accent-soft)',
          border: '1px solid var(--accent-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 700,
          color: 'var(--accent)',
          flex: 'none',
        }}
      >
        {n}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{sub}</div>
      </div>
    </>
  );
}

/** İndirme butonu gövdesi (tablo başlıklarında) — CSV ve PNG aynı görünümü paylaşır. */
function ExportButton({
  onClick,
  small,
  label,
  icon,
  title,
  disabled,
}: {
  onClick: () => void;
  small?: boolean;
  label: string;
  icon: string;
  title?: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: small ? '6px' : '7px',
        padding: small ? '7px 11px' : '8px 12px',
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: '9px',
        color: 'var(--dim)',
        fontSize: small ? '11.5px' : '12px',
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Icon name={icon} size={small ? 15 : 16} />
      {label}
    </button>
  );
}

/** "CSV" indirme butonu. */
export function CsvButton({ onClick, small }: { onClick: () => void; small?: boolean }) {
  return <ExportButton onClick={onClick} small={small} label="CSV" icon="download" />;
}

/** "XLSX" indirme butonu — CSV'nin Excel ikizi (xlsx-export.ts). */
export function XlsxButton({ onClick, small }: { onClick: () => void; small?: boolean }) {
  return <ExportButton onClick={onClick} small={small} label="XLSX" icon="grid_on" />;
}

/** "PNG" indirme butonu — kartı ekran görüntüsü olarak indirir (png-export.ts). `busy`
 *  çizim sürerken ikinci tıklamayı keser. */
export function PngButton({ onClick, small, busy }: { onClick: () => void; small?: boolean; busy?: boolean }) {
  return <ExportButton onClick={onClick} small={small} label="PNG" icon="image" title="Ekran görüntüsü olarak indir" disabled={busy} />;
}
